/**
 * Help Centre index — chapter "HELP CENTRE" of the Trust & Support ask.
 * Static, categorised, deliberately calm.
 */
import { useNavigate } from "react-router-dom";
import { BookOpen, ShieldCheck, Sparkles, Activity, LifeBuoy, Search, ArrowRight, MessageCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { isCustomerRole } from "@/constants/customerNav";

const CARDS = [
  { title: "Security & Privacy", body: "How SUNTRONIX ONE protects your data, controls, encryption, retention.", icon: ShieldCheck, to: "/help/trust" },
  { title: "Accessibility", body: "WCAG 2.2 AA design intent, keyboard support, reduced motion, contact.", icon: Sparkles, to: "/help/accessibility" },
  { title: "System Status", body: "Current operational status across authentication, API and integrations.", icon: Activity, to: "/help/status" },
  { title: "Contact Support", body: "Talk to your Customer Success Manager or open a support case.", icon: LifeBuoy, external: "mailto:support@suntronix.one" },
];

const CATEGORIES = [
  ["Getting started", "Take the tour, understand roles, first request."],
  ["Assets", "Register, edit, transfer, retire."],
  ["Employees", "Onboard, offboard, transfer, offboarding checklist."],
  ["Deployment", "Standard deployment · loaners · reservations."],
  ["Recovery", "Reverse pickup, inspection, warehouse receipt."],
  ["Service", "Repairs, RMA, warranty claims."],
  ["Reports", "Executive reports, exports, cost views."],
  ["Administration", "Users & roles, master data, imports."],
  ["Integrations", "MDM, HRIS, discovery connectors."],
  ["Security", "Sessions, password rotation, IP allow-list (roadmap)."],
];

const STAFF_CARD = { title: "WhatsApp quick-start (staff)", body: "Send an approved-template message in 3 steps: New Message → Ready template → Review & send. Status meanings explained.", icon: MessageCircle, to: "/admin/integrations/whatsapp?tab=messages&guide=1" };

export default function HelpCentre() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const internal = !!user?.role && !isCustomerRole(user.role) && !String(user.role).startsWith("supplier");
  const cards = internal ? [STAFF_CARD, ...CARDS] : CARDS;
  const filtered = CATEGORIES.filter(([t, d]) => (t + " " + d).toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="max-w-5xl space-y-8" data-testid="help-centre">
      <div>
        <div className="text-[10px] font-semibold tracking-[0.12em] uppercase text-slate-500">Help centre</div>
        <h1 className="text-2xl font-heading font-semibold text-slate-900 tracking-tight mt-1">How can we help?</h1>
        <p className="text-sm text-slate-600 mt-2 max-w-xl">Search for a topic, browse by category, or jump straight to the Trust and Accessibility centres.</p>

        <div className="mt-4 relative max-w-xl">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input placeholder="Search help topics…" className="pl-9 h-10" value={q} onChange={(e) => setQ(e.target.value)} data-testid="help-search" />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        {cards.map((c) => {
          const Icon = c.icon;
          return (
            <button
              key={c.title}
              onClick={() => c.external ? window.location.href = c.external : navigate(c.to)}
              className="text-left bg-white border border-slate-200 rounded-md p-5 hover:border-slate-300 hover:-translate-y-px hover:shadow-sm transition-[transform,box-shadow,border-color] group"
              data-testid={`help-card-${c.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            >
              <div className="h-8 w-8 rounded-md bg-slate-50 border border-slate-100 grid place-items-center">
                <Icon className="h-4 w-4 text-slate-700" strokeWidth={1.6} />
              </div>
              <div className="mt-3 text-sm font-heading font-semibold text-slate-900">{c.title}</div>
              <div className="text-xs text-slate-500 mt-1 leading-relaxed">{c.body}</div>
              <div className="mt-3 text-xs text-slate-500 flex items-center gap-1 group-hover:text-slate-900 transition-colors">
                Open <ArrowRight className="h-3 w-3" />
              </div>
            </button>
          );
        })}
      </div>

      <div>
        <div className="text-[10px] font-semibold tracking-[0.12em] uppercase text-slate-500 mb-3">Browse by category</div>
        <div className="bg-white border border-slate-200 rounded-md divide-y divide-slate-100">
          {filtered.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500">No topics match your search yet.</div>
          ) : filtered.map(([t, d]) => (
            <div key={t} className="px-4 py-3 flex items-start gap-3">
              <BookOpen className="h-4 w-4 mt-0.5 text-slate-400 shrink-0" strokeWidth={1.5} />
              <div className="min-w-0">
                <div className="text-sm text-slate-800 font-medium">{t}</div>
                <div className="text-xs text-slate-500 mt-0.5">{d}</div>
              </div>
              <span className="ml-auto text-[10px] font-semibold tracking-widest uppercase text-slate-400">Coming soon</span>
            </div>
          ))}
        </div>
      </div>

      <p className="text-[11px] text-slate-500">
        Detailed articles are being written iteratively with early customers. Until then your Customer Success Manager and support@suntronix.one are the fastest route to answers.
      </p>
    </div>
  );
}
